import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiArrowLeft, FiEdit2, FiSave, FiX, FiMapPin, FiPlus, FiLogOut, FiUser, FiPhone, FiMail } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import './UserInfoPage.css';

export default function UserInfoPage() {
  const navigate = useNavigate();
  const { user, logout, updateUser, fetchUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '' });
  const [addresses, setAddresses] = useState([]);
  const [newAddr, setNewAddr] = useState({ text: '', pincode: '' });
  const [addingAddr, setAddingAddr] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/auth'); return; }
    setForm({ name: user.name || '', phone: user.phone || '' });
    fetchAddresses();
  }, [user]);

  const fetchAddresses = async () => {
    try {
      const res = await axios.get('/api/auth/addresses');
      setAddresses(res.data.addresses || []);
    } catch {}
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name cannot be empty'); return; }
    setLoading(true);
    try {
      const res = await axios.put('/api/auth/profile', form);
      updateUser(res.data.user);
      setEditing(false);
      toast.success('Profile updated!');
    } catch (e) { toast.error(e.response?.data?.message || 'Update failed'); }
    finally { setLoading(false); }
  };

  const handleAddAddress = async () => {
    if (!newAddr.text.trim() || !newAddr.pincode.trim()) { toast.error('Enter address and pincode'); return; }
    if (addresses.length >= 10) { toast.error('Maximum 10 addresses allowed'); return; }
    try {
      await axios.post('/api/auth/addresses', newAddr);
      toast.success('Address added!');
      setNewAddr({ text: '', pincode: '' });
      setAddingAddr(false);
      fetchAddresses();
    } catch { toast.error('Failed to add address'); }
  };

  const handleDeleteAddress = async (idx) => {
    try {
      await axios.delete(`/api/auth/addresses/${idx}`);
      fetchAddresses();
      toast.success('Address removed');
    } catch { toast.error('Failed to remove address'); }
  };

  const handleLogout = () => { logout(); navigate('/'); toast.info('Logged out'); };

  if (!user) return null;

  return (
    <div className="page-container">
      <div className="uip-header">
        <button className="back-btn" onClick={() => navigate(-1)}><FiArrowLeft size={20}/></button>
        <span className="uip-logo" onClick={() => navigate('/')}>Abhiras</span>
        <button className="logout-icon-btn" onClick={handleLogout} title="Logout"><FiLogOut size={18}/></button>
      </div>

      <div className="uip-body">
        {/* Avatar */}
        <div className="uip-avatar-section">
          <div className="uip-avatar">
            {(user.name || user.username || 'A')[0].toUpperCase()}
          </div>
          <div>
            <h2 className="uip-display-name">{user.name || user.username}</h2>
            <p className="uip-username">@{user.username}</p>
          </div>
        </div>

        {/* Profile Card */}
        <div className="uip-card">
          <div className="uip-card-header">
            <h3><FiUser size={16}/> Personal Information</h3>
            {!editing
              ? <button className="edit-btn" onClick={() => setEditing(true)}><FiEdit2 size={14}/> Edit</button>
              : <div className="edit-actions">
                  <button className="save-btn" onClick={handleSave} disabled={loading}><FiSave size={14}/> Save</button>
                  <button className="cancel-btn" onClick={() => { setEditing(false); setForm({ name: user.name || '', phone: user.phone || '' }); }}><FiX size={14}/></button>
                </div>
            }
          </div>
          <div className="uip-fields">
            <div className="uip-field">
              <label><FiUser size={13}/> Full Name</label>
              {editing
                ? <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} />
                : <span>{user.name || '—'}</span>
              }
            </div>
            <div className="uip-field">
              <label><FiMail size={13}/> Username</label>
              <span>@{user.username}</span>
            </div>
            <div className="uip-field">
              <label><FiMail size={13}/> Email</label>
              <span>{user.email || '—'}</span>
            </div>
            <div className="uip-field">
              <label><FiPhone size={13}/> Phone</label>
              {editing
                ? <input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} placeholder="+91 98765 43210" />
                : <span>{user.phone || '—'}</span>
              }
            </div>
            <div className="uip-field">
              <label>Member Since</label>
              <span>{user.createddate ? new Date(user.createddate).toLocaleDateString('en-IN', { year:'numeric', month:'long' }) : '—'}</span>
            </div>
          </div>
        </div>

        {/* Addresses */}
        <div className="uip-card">
          <div className="uip-card-header">
            <h3><FiMapPin size={16}/> Saved Addresses</h3>
            {addresses.length < 10 && (
              <button className="edit-btn" onClick={() => setAddingAddr(a => !a)}><FiPlus size={14}/> Add</button>
            )}
          </div>
          {addingAddr && (
            <div className="addr-add-form">
              <textarea rows={3} placeholder="Full address (house no., street, city, state)" value={newAddr.text} onChange={e => setNewAddr(a => ({...a, text: e.target.value}))} />
              <input placeholder="Pincode" value={newAddr.pincode} onChange={e => setNewAddr(a => ({...a, pincode: e.target.value.replace(/\D/,'')}))} maxLength={6} />
              <div className="addr-form-actions">
                <button className="save-btn" onClick={handleAddAddress}>Save Address</button>
                <button className="cancel-btn" onClick={() => setAddingAddr(false)}>Cancel</button>
              </div>
            </div>
          )}
          {addresses.length === 0 && !addingAddr && <p className="no-addr">No saved addresses yet.</p>}
          {addresses.map((addr, i) => (
            <div key={i} className="addr-item">
              <FiMapPin size={14} color="var(--burgundy)" />
              <div className="addr-text">
                <p>{addr.text}</p>
                <p className="addr-pin">Pincode: {addr.pincode}</p>
              </div>
              <button className="addr-del" onClick={() => handleDeleteAddress(i)}><FiX size={14}/></button>
            </div>
          ))}
        </div>

        {/* Quick Links */}
        <div className="uip-card uip-links">
          <button onClick={() => navigate('/cart?tab=current')}><FiMapPin size={14}/> Current Orders</button>
          <button onClick={() => navigate('/cart?tab=previous')}><FiMapPin size={14}/> Previous Orders</button>
          <button onClick={() => navigate('/cart?tab=refunds')}><FiMapPin size={14}/> Refunds</button>
          <button onClick={() => navigate('/cart?tab=wishlist')}><FiMapPin size={14}/> Wishlist</button>
        </div>

        <button className="full-logout-btn" onClick={handleLogout}>
          <FiLogOut size={16}/> Logout
        </button>
      </div>
    </div>
  );
}
